import React, { useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { I18n } from 'react-redux-i18n';
import get from 'lodash/get';
import max from 'lodash/max';

import Carousel from 'components/Carousel';
import Header from 'components/Header';
import Kpi from 'components/Kpi';
import Spinner from 'components/Spinner';
import TabNavigation from 'components/TabNavigation';
import WarningMessageSmallScreen from 'components/WarningMessageSmallScreen';

import CarouselItem from 'modules/OnlineSales/components/CarouselItem';
import MinimumPrice from 'modules/OnlineSales/components/MinimumPrice';
import OnlineSalesBarChart from 'modules/OnlineSales/components/OnlineSalesBarChart';
import OnlineSalesDnLineChart from 'modules/OnlineSales/components/OnlineSalesDnLineChart';
import OnlineSalesFilters from 'modules/OnlineSales/components/OnlineSalesFilters';
import PointsOfSalesList from 'modules/OnlineSales/components/PointsOfSalesList';

import { getWebBasic, postPmcs } from 'actions/onlineSales';
import { SET_ONLINE_SALES_FILTERS } from 'constants/onlineSales';

const OnlineSalesContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  width: calc(100vw - 150px);
  padding: 20px;

  @media (max-width: ${(props) =>
      props.theme.devices.isTablet ? '0px' : '845px'}) {
    max-width: calc(100vw - 15px);
  }
  margin-bottom: 40px;

  .filters,
  .navigation {
    margin-top: 20px;
  }

  @media (min-width: 801px) {
    .carouselContent {
      line-height: 15px;
    }

    .pagination {
      margin-left: auto;
      margin-right: 13px;
      padding-left: 10px;
    }
  }

  .headerContainer {
    margin-top: 22px;
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  @media (min-width: 1201px) and (max-width: 1270px) {
    .headerContainer > div:first-child {
      height: 120px !important;
    }
  }

  @media (max-width: 1200px) {
    .pagination {
      margin-right: auto;
    }

    .headerContainer {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  .rightFeedContainer {
    display: flex;
    flex-direction: column;
    width: 100%;
    background-color: white;
  }
  .feedClientWines {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-top: 15px;
    margin-bottom: -10px;
    margin-left: 10px;
    z-index: 2;
  }
  .feedClientWines p {
    font-size: 12px;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    font-weight: 600;
  }
`;

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: calc(50% - 10px) calc(50% - 10px);
  grid-gap: 20px;
  margin-top: 20px;
  overflow: hidden;

  @media (max-width: 1250px) {
    grid-template-columns: 1fr;
  }

  .rightComponentsContainer {
    display: flex;
    flex-direction: row;
    width: 100%;
    align-items: center;

    > div:not(:last-child) {
      margin-right: 10px;
    }
  }

  .alignComponent {
    display: flex;
    align-items: center;

    p {
      margin-right: 10px;
    }

    div {
      text-transform: none;
    }
  }

  @media (max-width: ${(props) =>
      props.theme.devices.isTablet ? '0px' : '845px'}) {
    .rightComponentsContainer {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      margin-top: 10px !important;
    }
  }
`;

const OnlineSales = ({ getWebBasic, data }) => {
  const [loading, setLoading] = useState(true);
  const {
    kpis = [],
    feed = [],
    vintage: vintageApi,
    wine: wineApi,
    market: marketApi,
    filters
  } = data;

  const { wine, market, date } = useSelector(
    (state) => state.onlineSales.stateFilters
  );

  const [vintage, setVintage] = useState(null);
  const [currentKpi, setCurrentKpi] = useState(null);

  const [vintageBarchartVintage, setVintageBarchartVintage] = useState({
    vintageStart: null,
    vintageEnd: null
  });

  const [rivalBarchartVintage, setRivalBarchartVintage] = useState(null);
  const [rivalsDN, setRivalsDN] = useState([]);

  const exportOptions = { date, wine, market };

  useEffect(() => {
    (async () => {
      await getWebBasic();
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (kpis && kpis.length > 0) {
      dispatch({
        type: SET_ONLINE_SALES_FILTERS,
        payload: {
          wine: {
            value: get(data, 'filters.client_wines[0].wine_name'),
            label: get(data, 'filters.client_wines[0].wine_name')
          },
          date: get(kpis, '[0].date')
        }
      });
      if (!vintage || !vintage.value) {
        setVintage({ label: vintageApi, value: vintageApi });
      }
      setVintageBarchartVintage({ vintageStart: null, vintageEnd: null });
      setRivalsDN([]);
      setCurrentKpi(Math.floor(Math.random() * kpis.length));
    }
  }, [JSON.stringify(kpis)]);

  useEffect(() => {
    if (vintage && vintage.label !== vintageApi) {
      setVintage({ label: vintageApi, value: vintageApi });
    }
  }, [marketApi, wineApi]);

  useEffect(() => {
    let maxVintage = max(get(data, 'prices', []).map((item) => item.vintage));
    if (!vintage) {
      setVintage({ label: maxVintage, value: maxVintage });
    }
  }, [get(wine, 'value', null), get(market, 'value', null)]);

  useEffect(() => {
    (async () => {
      if (vintage && vintage.value !== vintageApi && !loading) {
        setLoading(true);
        await getWebBasic(
          wine ? wine.label : null,
          market ? market.label : null
        );
        setLoading(false);
      }
    })();
  }, [get(vintage, 'value')]);

  useEffect(() => {
    (async () => {
      if (wine && wine.value !== wineApi && !loading) {
        setLoading(true);
        await getWebBasic(
          wine ? wine.label : null,
          market ? market.label : null
        );
        setLoading(false);
      }
    })();
  }, [get(wine, 'value')]);

  useEffect(() => {
    (async () => {
      if (
        (!market && wine) ||
        (market && market.value !== marketApi && !loading)
      ) {
        setLoading(true);
        await getWebBasic(
          wine ? wine.label : null,
          market ? market.label : null
        );
        setLoading(false);
      }
    })();
  }, [get(market, 'value')]);

  useEffect(() => {
    if (wine) {
      setRivalsDN([]);
      setVintageBarchartVintage({ vintageStart: null, vintageEnd: null });
    }
  }, [JSON.stringify(data.prices)]);

  const getOptions = () => ({
    wines: get(data, 'filters.client_wines', []).map((item) => ({
      value: item.wine_name,
      label: item.wine_name
    })),
    markets: get(data, 'filters.markets', []).map((item) => ({
      value: item,
      label: item
    }))
  });

  const getRandomKpi = () =>
    kpis[currentKpi] || {
      wine_name: '',
      dn: 0,
      date: new Date()
    };

  const filterDataByWineAndMarket = (data, itemMarketKey) => {
    const wineName = wine
      ? wine.label
      : get(filters, 'client_wines[0].wine_name');

    return data.filter(
      (item) =>
        (item.wine_name === wineName ||
          (item.refs_client &&
            item.refs_client.map((ref) => ref.name).includes(wineName))) &&
        (!market || item[itemMarketKey] === market.value)
    );
  };

  const filterChartsData = (data) =>
    filterDataByWineAndMarket(data, 'market_name');

  const renderFilters = () => {
    const options = getOptions();
    return (
      <div className="filters">
        <OnlineSalesFilters
          wineFilter={{
            selectedOption: wine,
            options: options.wines,
            onChange: (value) =>
              dispatch({
                type: SET_ONLINE_SALES_FILTERS,
                payload: { wine: value }
              })
          }}
          marketFilter={{
            selectedOption: market,
            options: [
              { label: I18n.t('placeholderAll'), value: null },
              ...options.markets
            ],
            onChange: (marketItem) =>
              dispatch({
                type: SET_ONLINE_SALES_FILTERS,
                payload: {
                  market:
                    marketItem && marketItem.value === null ? null : marketItem
                }
              })
          }}
        />
      </div>
    );
  };

  const handleKpiNext =
    kpis && kpis.length > 1
      ? () =>
          setCurrentKpi(
            currentKpi === kpis.length - 1 || kpis.length < 2
              ? 0
              : currentKpi + 1
          )
      : undefined;

  const getRivalList = ({ dataToFilter, customWine, filterByVintage }) => {
    const { filters = { client_wines: [] } } = data;
    const wineData = filters.client_wines.find(
      (item) =>
        (wine && item.wine_name === wine.label) ||
        (customWine && item.wine_name === customWine.label)
    );
    const rivals =
      wineData && wineData.rivals && !wineData.rivals[0] !== null
        ? wineData.rivals
        : [];

    return [
      ...new Set(
        dataToFilter
          .filter(
            (item) =>
              (!market || item.market_name === market.value) &&
              rivals.includes(item.wine_name) &&
              (!filterByVintage || item.vintage === vintageApi)
          )
          .map((item) => item.wine_name)
      )
    ];
  };

  const dispatch = useDispatch();

  const savePmcs = async (pmcs) => {
    setLoading(true);
    await dispatch(postPmcs(pmcs));
    setLoading(false);
  };

  const renderContent = () => {
    const {
      prices = [],
      dn_history = [],
      store_references = [],
      pmc_choices = {}
    } = data;

    const navigation = [
      {
        label: I18n.t('onlineSales.tabOverview'),
        route: '/distribution/online-sales/overview'
      },
      {
        label: I18n.t('onlineSales.tabDetailed'),
        route: '/distribution/online-sales/detailed'
      }
    ];

    return (
      <>
        <div className="navigation">
          <TabNavigation buttons={navigation} />
        </div>
        <MinimumPrice
          isAdmin
          vintageOptions={get(pmc_choices, 'vintages', []).map((item) => ({
            value: item,
            label: item
          }))}
          currencyOptions={get(pmc_choices, 'currencies', []).map((item) => ({
            value: item,
            label: item
          }))}
          savePmcs={(pmcs) => savePmcs(pmcs)}
          wine={wine ? wine.label : ''}
        />
        <Wrapper>
          <OnlineSalesBarChart
            premium
            exportOptions={{
              ...exportOptions,
              title: I18n.t('onlineSales.barGraphVintageTitle')
            }}
            allMarket={!market}
            title={I18n.t('onlineSales.barGraphVintageTitle')}
            dataClient={filterChartsData(prices)}
            data={prices.filter(
              (item) => !market || item.market_name === market.value
            )}
            handleVintage={(field, value) =>
              setVintageBarchartVintage({
                ...vintageBarchartVintage,
                [field]: value
              })
            }
            rivalList={getRivalList({
              dataToFilter: prices
            })}
            rivalSelection={rivalBarchartVintage}
            handleRivalSelection={setRivalBarchartVintage}
            vintageStart={vintageBarchartVintage.vintageStart}
            vintageEnd={vintageBarchartVintage.vintageEnd}
            showLocalCurrency={false}
          />
          <OnlineSalesDnLineChart
            premium
            exportOptions={{
              ...exportOptions,
              title: I18n.t('onlineSales.lineGraphDnTitle')
            }}
            allMarket={!market}
            title={I18n.t('onlineSales.lineGraphDnTitle')}
            dataClient={filterChartsData(dn_history)}
            data={dn_history.filter(
              (item) => !market || item.market_name === market.value
            )}
            rivalSelection={rivalsDN}
            rivalList={getRivalList({
              dataToFilter: dn_history
            })}
            handleRivalSelection={(value) =>
              setRivalsDN(value && value.length > 5 ? value.slice(1, 6) : value)
            }
          />
          <PointsOfSalesList
            data={store_references}
            retractedSize={7}
            exportOptions={{
              ...exportOptions,
              title: I18n.t('onlineSales.posTitle')
            }}
            premium
          />
        </Wrapper>
      </>
    );
  };

  const kpi = getRandomKpi();

  return (
    <OnlineSalesContainer>
      <Header
        title={I18n.t('onlineSales.title')}
        subTitle={
          <>
            {I18n.t('onlineSales.subTitle1')}{' '}
            <span> {I18n.t('onlineSales.subTitleStrong')} </span>
            {I18n.t('onlineSales.subTitle2')}
          </>
        }
      />
      <div className="headerContainer">
        <Kpi
          title={kpi.wine_name}
          valueText={I18n.t('onlineSales.kpiValueText')}
          value={kpi.dn}
          date={kpi.date}
          dateText={`${I18n.t('onlineSales.kpiDateText')} `}
          handleNext={handleKpiNext}
          tooltip={I18n.t('onlineSales.tooltip')}
        />
        <Carousel ItemComponent={CarouselItem} data={feed} />
      </div>
      {window.innerWidth > 845 ? (
        <React.Fragment>
          {renderFilters()}
          {renderContent()}
        </React.Fragment>
      ) : (
        <WarningMessageSmallScreen />
      )}
      <Spinner loading={loading} />
    </OnlineSalesContainer>
  );
};

const mapStateToProps = (state) => {
  return {
    data: state.onlineSales
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getWebBasic: async (wine, market) => {
      await dispatch(getWebBasic(wine, market));
    },
    postPmcs: async (pmcs) => {
      await dispatch(postPmcs(pmcs));
    }
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(OnlineSales);
